<template lang="pug">
.img-container(ref="imgContainerRef")
  .product-img(ref="imgRef")
    DynamicMedia(:config=`product.sku === 'RA2752-CONFIG' ? bikeHeroImg : heroImg`)
      ImageFlag.product-media-flag(
        v-if="product && product.productId",
        :product="product"
      )
</template>
<script setup lang="ts">
import { computed, onMounted, ref, nextTick } from 'vue'
import { useResizeObserver } from '@vueuse/core'

// Width of the background image
const BG_WIDTH = 5466

const IMG_WIDTH = 1466
const IMG_HEIGHT = 1466

const imgContainerRef = ref<HTMLElement>()
const imgRef = ref<HTMLElement>()

const props = defineProps({
  img: {
    type     : Object,
    required : true,
  },
  product: {
    type    : Object,
    default : () => ({}),
  },
})

const heroImg = computed(() => ({
  src    : props.img.link,
  width  : IMG_WIDTH,
  height : IMG_HEIGHT,
}))

const bikeHeroImg = computed(() => {
  if (props.product.sku === 'RA2752-CONFIG') {
    return {
      src    : props.img.link,
      width  : 2400,
      height : 1600,
    }
  }

  return null
})

function update() {
  if (!imgRef.value) {
    return
  }
  // Get the position of the main image
  const box = imgRef.value.getBoundingClientRect()
  const el = document.querySelector('.grid-media-overflow-bg')
  // Use scale, centering, and offset to adjust background-position-x

  if (props.product.sku === 'RA2752-CONFIG') {
    return
  }

  // Get the scale of the image compared to the scale of the image height
  const scale = box.height / IMG_HEIGHT
  const centeredOnLeftEdge = -1 * ((scale * BG_WIDTH) / 2) + (270 * scale) // RM-43 images were taken 270px from center
  const rmOffset = box.left + (box.width / 2)

  if (el) {
    el.setAttribute('style', `background-position-x:${centeredOnLeftEdge + rmOffset}px`)
  }
}

useResizeObserver(imgContainerRef, update)

onMounted(() => {
  nextTick(() => update())
})

</script>
<style lang="scss">
/**
* This component will modify the product page layout for specific products
* It is similar to a Scalable layout, but with a specific background and offset.
* To add a new product, add the product class for breadcrumbs, margin/padding,
* and provide a specific background image. You may need to offset the position
* in the update().
*
* @TODO Lots of duplicate code from scalable image in sisu...
*
* - Overflow background img is hardcoded.
* - Watches the size of the screen and adjusts the background position to match
* - Since the rm-43 product image have a shadow to the left, they are offset-left on mobile
 */

//  Add the new product class if white breadcrumbs are desired
.product-rm-43, .product-rf0971-black-mt, .product-ra2752-config {
  .product-page .crumbs {
    color: white;

    a.crumb-link {
      color: white;
    }
  }
}

// Add the new product class for margin and padding styles
.product-rm-43, .product-rf0971-black-mt, .product-ra2752-config {

 .product-page .product-pg {

  .img-container .rg-img {
    background-color: transparent;
  }

  @include not-desktop(){
    // Mobile view was offsetting rm-43 way to the right, because the thumbs are not centered (shadows)
    // Negative left margin will push it back to the center.
    .product-rm-43 .grid-media, .product-rf0971-black-mt .grid-media {
      margin-left: -20%;
    }

    .product-rm-43 .grid-media-overflow-bg, .product-rf0971-black-mt .grid-media-overflow-bg {
      margin-bottom: $space-s;
    }
  }

  @include tablet(){
    .grid-media-overflow-bg {
      margin-bottom: $space-l;
    }
  }

  @include all-desktop() {
    .grid-media {
      margin-top: 0;
      margin-bottom: 0;
    }

    .grid-rhpa, .grid-media-overflow-bg {
      margin-bottom: 0;
    }

    .grid-full {
      margin-top: 0
    }

    .grid-tabs {
      margin-bottom: 0
    }

    .grid-micro {
      margin-top: 0
    }
  }
}
}

// Add the new product class and provide the background url
// RM-43 & Ryan Davis Signature
.product-rm-43, .product-rf0971-black-mt {
  .grid-media-overflow-bg {
    background-color: black;
    // Barbell Club wall bg
    background-image: url("https://assets.roguefitness.com/f_auto,q_auto/dynamic/rm43/bg_x2axkn.jpg");
    background-repeat: no-repeat;
    // Height 100% means we do not have to scale the bg separately from the product images
    background-size: auto 100%;
  }
}

// The Milo
.product-ra2752-config  {
  .grid-media-overflow-bg {
    background-color: black !important;
    background-image: url("https://assets.roguefitness.com/image/upload/v1720547128/catalog/Conditioning/Endurance%20/Bikes/The%20Milo/Bike-Deuce-Header-test2_s71cbj.png");
    background-repeat: no-repeat;
    background-size: auto 100%;
  }

  @media screen and (min-width: 1259px) and (max-width: 1840px) {
    .grid-media-overflow-bg {
      background: none;
    }

    .media-wrap {
      margin-top: 55px;
      width: 100vw;
    }
  }

  @media screen and (min-width: 1024px) and (max-width: 1258px) {
    .cms-row.mt-none.mb-none.normal  {
      padding-top: 220px;
    }

    #pdp-media {
      position: relative;
      top: 0;
      left: 0;
      width: 960px;
      height: 640px;
    }

    #pdp-rhpa {
      width: 100%;
      position: absolute;
      top: 9.25%;
      left: 0;
      padding-top: 15px;
    }
  }
}
</style>

<style lang="scss" scoped>
.img-container{
  display: flex;
  justify-content: center;
}
.product-img {
  width: 100%;
  height: 100%;
  // Update to change the max height of the image

  @include xl(){
    .product-rm-43, .product-rf0971-black-mt & {
      max-height: 936px;
      max-width: 936px;
    }
  }

  @include large(){
    .product-rm-43, .product-rf0971-black-mt & {
      max-height: 850px;
      max-width: 850px;
    }
  }

  @include desktop(){
    .product-rm-43, .product-rf0971-black-mt & {
      max-height: 800px;
      max-width: 800px;
    }
  }

  @include tablet(){
    .product-rm-43, .product-rf0971-black-mt & {
      max-height: 600px;
      max-width: 600px;
    }
  }
}

.product-media-flag.flag-container {
  position: absolute;
  right: 0px;
  top: 55px;
  z-index: 1;

  @include large() {
    right: 0px;
  }

  @include not-desktop() {
    right: $space-s;
    top: $space-xxs;
  }
}
</style>
